/**
 * Guided listing setup state (BR-107, BR-110): the rail's per-step status and one-line summary,
 * derived ONLY from saved data. It replaces the four-row first-publish checklist state.
 *
 * Completion predicates (a closed set):
 *   marketing  the BR-113 minimum is met (description plus one approved photo)
 *   details    a draft Listing exists for the Unit
 *   showings   the Showings answer is saved AND at least one weekly rule, private-showing date or open
 *              house is saved
 *   contact    contact name AND phone are saved on the Listing
 *   agent      enablement and review are saved explicitly at listing scope
 *   preview    never done: it has no saved data and never gates resume
 *   review     never done before publish
 * Before the Listing exists only Marketing can show as done. Status never blocks navigation.
 */

import { useQuery } from '@tanstack/react-query';
import { SMOKING_POLICY_LABELS, labelFor, todayInTimezone } from '@resida/shared';
import { useMarketingProfile } from '../../../hooks/useMarketingProfile';
import { unitPhotosQueryOptions } from '../../../hooks/useUnitPhotos';
import { fetchListing, type Listing, type ScreeningCriteria } from '../../../lib/leasing.api';
import { computeMarketingCompleteness, type MarketingCompleteness } from '../../../lib/marketingCompleteness';
import { MANAGER_PROFILE_QUERY } from '../listing-detail/ContactInfoEditor';
import { availabilityQueryOptions, openHousesQueryOptions } from '../listing-detail/showings/availability.query';
import { describeShowings, impliedShowingsAnswer, isShowingsComplete } from '../listing-detail/showings/showings-mode';
import { formatDate, formatPhoneDisplay, formatRentCents } from '../listing-detail/shared';
import { useListingAgentPolicy } from '../listing-detail/useListingAgentPolicy';
import {
  GUIDE_STEPS,
  firstIncompleteStep,
  type GuideStepKey,
  type GuideStepState,
  type GuideStepStatus,
} from './guide-steps';

export interface GuideState {
  steps: Record<GuideStepKey, GuideStepState>;
  /** The saved-data predicate per step, before the current-step overlay (Preview and Review are false). */
  done: Record<GuideStepKey, boolean>;
  marketing: MarketingCompleteness;
  /** Optional Screening criteria row of the readiness panel / review. */
  screening: { status: GuideStepStatus; summary: string; loading: boolean };
  /** Where the guide opens: first incomplete step in BR-107 order, skipping Preview; Review when all done. */
  firstIncomplete: GuideStepKey;
  isLoading: boolean;
}

const BEFORE_LISTING = 'Save listing details first.';

export function describeScreening(criteria: ScreeningCriteria | null): string {
  if (!criteria) return 'Optional. No screening criteria set.';
  const parts: string[] = [];
  if (criteria.minIncomeMultiple != null) parts.push(`Income at least ${criteria.minIncomeMultiple}× rent`);
  if (criteria.minCreditScore != null) parts.push(`Credit ${criteria.minCreditScore}+`);
  if (criteria.maxOccupants != null) parts.push(`Up to ${criteria.maxOccupants} occupants`);
  if (parts.length === 0 && criteria.smokingPolicy) {
    parts.push(`Smoking: ${labelFor(SMOKING_POLICY_LABELS, criteria.smokingPolicy)}`);
  }
  if (parts.length === 0) parts.push('Criteria saved');
  // BR-102: the landlord must see at a glance that prospects won't find these in the listing.
  if (criteria.publishScreeningCriteria === false) parts.push('Not published');
  return parts.join(' · ');
}

function contactSummary(
  listing: Listing,
  prefill: { name: string | null; phone: string | null } | null
): { done: boolean; summary: string } {
  const name = listing.contactName?.trim() ?? '';
  const phone = listing.contactPhone?.trim() ?? '';
  if (name && phone) return { done: true, summary: `${name} · ${formatPhoneDisplay(phone)}` };
  // Part saved: show it, and say which half is missing.
  if (name) return { done: false, summary: `${name} · add a phone. Required to publish.` };
  if (phone) return { done: false, summary: `${formatPhoneDisplay(phone)} · add a name. Required to publish.` };
  const fromAccount = [prefill?.name, formatPhoneDisplay(prefill?.phone)].filter((v): v is string => !!v?.trim());
  if (fromAccount.length > 0) {
    return { done: false, summary: `From your account: ${fromAccount.join(' · ')}. Save to use it.` };
  }
  return { done: false, summary: 'Add a contact name and phone. Required to publish.' };
}

export function useGuideState(args: {
  unitId: string;
  /** null until Listing details is first saved. */
  listing: Listing | null;
  /** The step on screen; a not-yet-done current step reads `current`. */
  currentStep?: GuideStepKey;
}): GuideState {
  const { unitId, listing, currentStep } = args;
  const listingId = listing?.id ?? '';

  const profileQuery = useMarketingProfile(unitId);
  const { data: photos, isLoading: photosLoading } = useQuery({ ...unitPhotosQueryOptions(unitId), enabled: unitId !== '' });
  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['listing', listingId],
    queryFn: () => fetchListing(listingId),
    enabled: listingId !== '',
  });
  const canManage = detail?.permissions.canManage ?? false;
  const { data: availability, isLoading: availabilityLoading } = useQuery({
    ...availabilityQueryOptions(listingId),
    enabled: listingId !== '',
  });
  const { data: openHouses, isLoading: openHousesLoading } = useQuery({
    ...openHousesQueryOptions(listingId),
    enabled: listingId !== '',
  });
  const { data: managerProfile } = useQuery(MANAGER_PROFILE_QUERY);
  const { data: agentPolicy, isLoading: agentLoading } = useListingAgentPolicy(listingId, canManage);

  const marketing = computeMarketingCompleteness(profileQuery.profile, photos ?? []);
  const marketingLoading = profileQuery.isLoading || photosLoading;

  const prefill = managerProfile
    ? {
        name:
          [managerProfile.firstName, managerProfile.lastName].filter((part): part is string => !!part).join(' ') ||
          null,
        phone: managerProfile.phone ?? null,
      }
    : null;

  const done: Record<GuideStepKey, boolean> = {
    marketing: marketing.minimumMet,
    details: listing != null,
    showings: false,
    contact: false,
    agent: false,
    preview: false,
    review: false,
  };

  const steps = {} as Record<GuideStepKey, GuideStepState>;

  steps.marketing = {
    status: marketing.minimumMet ? 'done' : marketing.partsDone > 0 ? 'needs_attention' : 'todo',
    summary: `${marketing.percent}% complete · ${marketing.partsDone} of ${marketing.partsTotal} parts`,
    loading: marketingLoading,
  };

  steps.details = listing
    ? {
        status: 'done',
        summary: `${formatRentCents(listing.askingRentCents)}/mo · available ${formatDate(listing.availableDate)}`,
        loading: false,
      }
    : { status: 'todo', summary: 'Rent, lease terms and move-in costs.', loading: false };

  if (!listing) {
    for (const key of ['showings', 'contact', 'agent', 'preview', 'review'] as const) {
      steps[key] = { status: 'todo', summary: BEFORE_LISTING, loading: false };
    }
  } else {
    const rules = availability?.rules ?? [];
    const exceptions = availability?.exceptions ?? [];
    const houses = openHouses?.items ?? [];
    // A private-showing date that has passed offers no bookable time: count only today and later, in
    // the listing's timezone (the same cut-off the Showings step uses).
    const today = openHouses ? todayInTimezone(openHouses.timezone) : undefined;
    // A live listing with times but no saved answer predates the answer: its times imply one (BR-107 only
    // asks a DRAFT to say it), so its Overview summary keeps showing them.
    const answer =
      listing.showingsAnswer ?? (listing.status !== 'draft' ? impliedShowingsAnswer(rules, exceptions, houses) : null);
    done.showings = availability != null && openHouses != null && isShowingsComplete(answer, rules, exceptions, houses, today);
    steps.showings = {
      status: done.showings ? 'done' : 'needs_attention',
      summary:
        availability == null || openHouses == null
          ? 'Checking showing times…'
          : done.showings
            ? describeShowings(rules, exceptions, houses, listing.defaultShowingDurationMinutes ?? null, today)
            : !answer && (rules.length > 0 || exceptions.length > 0 || houses.length > 0)
              ? 'Say whether you are generally available to show it.'
              : "No showing times yet. Prospects can't book until you add some.",
      loading: availabilityLoading || openHousesLoading,
    };

    const contact = contactSummary(listing, prefill);
    done.contact = contact.done;
    steps.contact = {
      status: contact.done ? 'done' : 'needs_attention',
      summary: contact.summary,
      loading: detailLoading,
    };

    const explicit = agentPolicy != null && agentPolicy.enablement !== 'inherit' && agentPolicy.review !== 'inherit';
    done.agent = explicit;
    const agentOn = agentPolicy?.resolvedEnablement === 'enabled';
    steps.agent = {
      status: explicit ? 'done' : 'todo',
      summary: !canManage && !detailLoading
        ? 'Only people who manage this listing can see this.'
        : agentPolicy == null
          ? 'Starts at your default settings.'
          : agentOn
            ? `On · ${agentPolicy.resolvedReview === 'required' ? 'replies wait for your review' : 'replies send without review'}`
            : 'Off · prospects get no agent replies',
      loading: canManage && agentLoading,
    };

    steps.preview = { status: 'todo', summary: 'See what prospects will see.', loading: false };
    steps.review = { status: 'todo', summary: 'One last look, then pay the listing fee.', loading: false };
  }

  // Overlay the step on screen: a step that is not done yet reads "current".
  for (const { key } of GUIDE_STEPS) {
    if (key === currentStep && steps[key].status !== 'done') steps[key] = { ...steps[key], status: 'current' };
  }

  const screeningCriteria = detail?.screeningCriteria ?? null;
  return {
    steps,
    done,
    marketing,
    screening: {
      status: screeningCriteria ? 'done' : 'todo',
      summary: describeScreening(screeningCriteria),
      loading: listingId !== '' && detailLoading,
    },
    firstIncomplete: firstIncompleteStep(done),
    isLoading: GUIDE_STEPS.some(({ key }) => steps[key].loading),
  };
}
